import React, {Component} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {InlineLinkButton} from '../../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {openInBrowser} from '../../../../../srcCommon/browser/openInBrowser';
import {TrueAvailabilityStatusesEnum} from '../../../../helpers/models/schedulingLinkAvailabilityHelpers';
import {
  ComposerBannerBubble,
  ComposerBannerThemesEnum,
} from '../../../inboxes/composer/banner/composerBannerBubble';

/*
 * Props.
 */

interface SchedulingAvailabilityBannerProps {
  availabilityStatus: TrueAvailabilityStatusesEnum;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  position: relative;
`;

const StyledDescriptionDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  padding: 10px 0px 10px 10px;
`;

/*
 * Component.
 */

interface SchedulingAvailabilityBannerState {
  lastAvailabilityStatus?: TrueAvailabilityStatusesEnum;
  isDismissed: boolean;
}

export class SchedulingAvailabilityBanner extends Component<
  SchedulingAvailabilityBannerProps,
  SchedulingAvailabilityBannerState
> {
  constructor(props: SchedulingAvailabilityBannerProps) {
    super(props);
    this.state = {isDismissed: false};
  }

  /*
   * Event handlers.
   */

  private readonly dismiss = () => {
    this.setState({isDismissed: true});
  };

  /*
   * Lifecycle.
   */

  static getDerivedStateFromProps(
    props: Readonly<SchedulingAvailabilityBannerProps>,
    state: Readonly<SchedulingAvailabilityBannerState>,
  ) {
    if (state.lastAvailabilityStatus && props.availabilityStatus !== state.lastAvailabilityStatus) {
      return {lastAvailabilityStatus: props.availabilityStatus, isDismissed: false};
    }

    return null;
  }

  /*
   * Render.
   */

  render() {
    if (this.state.isDismissed) {
      return null;
    }

    const description = renderDescriptionForAvailability(this.props.availabilityStatus);
    if (!description) {
      return null;
    }

    return (
      <StyledWrapperDiv>
        <ComposerBannerBubble
          bannerTheme={ComposerBannerThemesEnum.ORANGE}
          shouldShowWarningIcon
          onDismiss={this.dismiss}
        >
          <StyledDescriptionDiv>{description}</StyledDescriptionDiv>
        </ComposerBannerBubble>
      </StyledWrapperDiv>
    );
  }
}

/*
 * Helpers.
 */

function renderDescriptionForAvailability(availabilityStatus: TrueAvailabilityStatusesEnum) {
  switch (availabilityStatus) {
    case TrueAvailabilityStatusesEnum.NO_AVAILABILITY:
      return (
        <>
          <Trans id="scheduling-insertmeetingtimes-noavailability">
            No available meeting times - your slots may conflict with events on your selected calendars.
          </Trans>
          &nbsp;
          <InlineLinkButton
            onClick={() =>
              openInBrowser(
                'https://help.frontapp.com/t/h4bz9h/troubleshooting-my-scheduling-link-doesnt-show-the-slots-i-expect-it-to',
              )
            }
          >
            <Trans id="scheduling-learn-more">Learn more</Trans>
          </InlineLinkButton>
        </>
      );
    case TrueAvailabilityStatusesEnum.PAST_AVAILABILITY:
      return (
        <Trans id="scheduling-insertmeetingtimes-pastavailability">
          These times are no longer available. Try picking some new options.
        </Trans>
      );
    default:
      return null;
  }
}
